import { ARENA_HEIGHT, ARENA_WIDTH } from '../game/asteroid-field';
import { getCharacter } from '../game/characters';
import { mergeEntries, type LeaderboardEntry } from '../game/leaderboard';
import { KoodooScene, TEXT_STYLES } from './base-scene';

const ROW_HEIGHT = 46;
const FIRST_ROW_Y = 190;

/**
 * The high score board.
 *
 * Scores are global and permanent — see docs/global-leaderboard.md. The cached
 * local board is drawn first so the screen is never blank, then replaced when
 * the global one arrives. If it never arrives the cache is what stays, labelled
 * honestly rather than passed off as the real board.
 */
export class HighscoresScene extends KoodooScene {
  static readonly KEY = 'Highscores';

  private rows: Phaser.GameObjects.GameObject[] = [];
  private status?: Phaser.GameObjects.Text;

  constructor() {
    super(HighscoresScene.KEY);
  }

  preload(): void {
    this.loadBackgrounds();
    this.load.image('back_button', 'assets/images/ui-objects/back-icon.png');
  }

  create(): void {
    // A race hands straight over to the board, so this is often the first
    // menu screen after the race has stopped all music.
    this.playMenuTheme();
    this.addBackgrounds();
    this.setCursor('generic');
    this.rows = [];

    this.add
      .text(ARENA_WIDTH / 2, 90, 'HIGH SCORES', { ...TEXT_STYLES.menu, fontSize: '52px' })
      .setOrigin(0.5);

    this.status = this.add
      .text(ARENA_WIDTH / 2, ARENA_HEIGHT - 70, 'loading the global board...', {
        ...TEXT_STYLES.hint,
        fontSize: '18px',
      })
      .setOrigin(0.5);

    this.renderRows(mergeEntries([], this.session.getHighscores()));
    void this.loadBoard();

    this.addBackButton();
  }

  private async loadBoard(): Promise<void> {
    // Any scores this browser set before the global board existed go up first,
    // so the player does not watch their own history be omitted.
    await this.session.syncLocalScoresOnce();
    const { entries, global } = await this.session.loadGlobalBoard();

    // The player may well have left while that was in flight.
    if (!this.scene.isActive()) {
      return;
    }

    this.renderRows(entries);
    this.status?.setText(
      global ? 'global board' : 'offline - showing this device only',
    );
  }

  private renderRows(entries: readonly LeaderboardEntry[]): void {
    for (const row of this.rows) {
      row.destroy();
    }
    this.rows = [];

    if (entries.length === 0) {
      this.rows.push(
        this.add
          .text(ARENA_WIDTH / 2, ARENA_HEIGHT / 2, 'No scores yet.\nGo and set one.', {
            ...TEXT_STYLES.menu,
            fontSize: '30px',
            align: 'center',
          })
          .setOrigin(0.5),
      );
      return;
    }

    for (const [index, entry] of entries.entries()) {
      const y = FIRST_ROW_Y + index * ROW_HEIGHT;
      const dim = index === 0 ? 1 : 0.78;
      const style = { ...TEXT_STYLES.menu, fontSize: index === 0 ? '32px' : '26px' };

      this.rows.push(
        this.add
          .text(340, y, `${index + 1}.`, style)
          .setOrigin(1, 0.5)
          .setAlpha(dim),
        this.add.text(380, y, entry.initials, style).setOrigin(0, 0.5).setAlpha(dim),
        this.add
          .text(560, y, getCharacter(entry.character).displayName, {
            ...style,
            fontSize: '22px',
          })
          .setOrigin(0, 0.5)
          .setAlpha(dim * 0.8),
        this.add
          .text(940, y, `${entry.score}`, style)
          .setOrigin(1, 0.5)
          .setAlpha(dim),
      );
    }
  }

  private addBackButton(): void {
    const back = this.add.image(80, ARENA_HEIGHT - 80, 'back_button').setInteractive();
    back.setAlpha(0.8);
    back.on('pointerover', () => {
      back.setAlpha(1);
      this.session.audio.playEffect('mouse_over');
    });
    back.on('pointerout', () => back.setAlpha(0.8));
    back.on('pointerup', () => {
      this.session.audio.playEffect('mouse_click');
      this.scene.start('MainMenu');
    });
  }
}
